import {
  IconArrowLeft,
  IconArrowsRight,
  IconDiscord,
  IconDiscordMid,
  IconEmoji2,
  IconEmoji3,
  IconEmoji4,
  IconMuseSolid,
} from '@/components/icons'
import { classNames } from '@/lib/classNames'
import { CheckIcon } from '@heroicons/react/24/outline'
import axios, { AxiosResponse } from 'axios'
import { UserProfile } from 'hooks/useUserProfile'
import { signIn } from 'next-auth/react'
import { useCallback, useState } from 'react'
import { KeyedMutator } from 'swr'
import { HomeViewType } from 'types/home'
import { useHomeViewType } from './MainContent'

export interface HomePageProps {
  userProfile?: UserProfile
  onGetUserProfile?: KeyedMutator<AxiosResponse<UserProfile, any>>
  needLogin?: boolean
}

enum Step {
  Login,
  Bind,
  Invite,
}

const steps = [
  {
    name: '登录 Muse',
    value: Step.Login,
    index: '1',
  },
  {
    name: '绑定/解绑 Discord',
    value: Step.Bind,
    index: '2',
  },
  {
    name: '邀请 MuseAI Bot 进频道',
    value: Step.Invite,
    index: '3',
  },
]
export default function DiscordContent({ userProfile, onGetUserProfile, needLogin }: HomePageProps) {
  const [_, setHomeViewType] = useHomeViewType()
  const { discordUserId, discordUsername } = userProfile?.userDiscord || {}
  const [step, setStep] = useState(Step.Login)

  const needBindDiscord = !discordUserId

  // methods for discord
  const unbind = useCallback(async () => {
    await axios.post('/api/auth/unbind')
    onGetUserProfile?.()
  }, [onGetUserProfile])

  return (
    <>
      <div className="relative flex flex-col md:min-h-screen justify-center p-0">
        <div className="sm:mx-auto sm:w-full flex flex-col justify-center items-center pt-5 md:pt-10 px-5 sm:px-10 mb-[40px] md:mb-[30px]">
          <div className="flex justify-center space-x-4 items-center mb-3">
            <IconDiscordMid className="w-[94px] h-[46px]" />
            <IconArrowsRight className="w-[28px] h-[15px]" />
            <IconMuseSolid className="w-[50px] h-[50px]" />
          </div>
          <h2 className="text-center text-[32px] font-normal tracking-tight">MuseAI Discord Bot</h2>
        </div>

        {/* steps */}
        <ol role="list" className="hidden md:flex justify-center items-center mb-[60px]">
          {steps.map((stepItem, stepIdx) => (
            <li
              key={stepItem.value}
              className={classNames(stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-[160px]' : '', 'relative')}
            >
              {stepItem.value < step ? (
                // 已完成的步骤
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <button
                      className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full bg-accent-600 hover:bg-indigo-900"
                      onClick={() => setStep(stepItem.value)}
                    >
                      <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                      <span className="sr-only">{stepItem.name}</span>
                    </button>
                    <div className="w-[38px] relative text-basic-800 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              ) : stepItem.value === step ? (
                // 当前步骤
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <span
                      className="relative flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-accent-600 bg-white"
                      aria-current="step"
                    >
                      <span className="text-accent-600 text-lg leading-5" aria-hidden="true">
                        {stepItem.index}
                      </span>
                      <span className="sr-only">{stepItem.name}</span>
                    </span>
                    <div className="w-[38px] relative text-basic-800 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute left-[44px] right-[6px] h-[38px] flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-basic-400" />
                  </div>
                  <div className="inline-flex flex-col space-y-2 items-center">
                    <button
                      className="group relative flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-basic-400 bg-white"
                      onClick={() => {
                        if (stepItem.value === step + 1) {
                          setStep(stepItem.value)
                        }
                      }}
                    >
                      <span className="text-basic-500 text-lg leading-5" aria-hidden="true">
                        {stepItem.index}
                      </span>
                      <span className="sr-only">{stepItem.name}</span>
                    </button>
                    <div className="w-[38px] relative text-basic-500 font-medium h-[24px] overflow-visible">
                      <span className="absolute left-1/2 top-0 leading-[24px] whitespace-nowrap transform -translate-x-1/2">
                        {stepItem.name}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ol>

        <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-4xl px-5 mb-24 flex justify-center">
          <div
            className={classNames(
              `bg-white px-4 py-6 rounded-xl shadow sm:rounded-lg transition-shadow relative overflow-hidden w-[312px] mx-auto hover:shadow-xl`,
              'flex flex-col justify-between items-center space-y-[40px]',
            )}
          >
            {step !== Step.Bind && step !== Step.Invite ? (
              needLogin ? (
                <>
                  <div className="text-center text-sm text-black">需要先登录噢~</div>
                  <span className="block text-[54px] leading-[54px]">🤣</span>
                  <div className="w-full">
                    <button
                      className="bg-accent-600 w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        signIn('muse')
                      }}
                    >
                      去登录 Muse
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center text-sm text-black">真棒！</div>
                  <IconEmoji3 className="w-[54px] h-[54px]" />
                  {/* <span className="block text-[54px] leading-[54px]">😊</span> */}
                  <div className="w-full">
                    <button
                      className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        setStep(Step.Bind)
                      }}
                    >
                      已登录，下一步
                    </button>
                  </div>
                </>
              )
            ) : step === Step.Bind ? (
              needBindDiscord ? (
                <>
                  <div className="text-center text-sm text-black">最后一步啦👇🥳~</div>
                  <IconEmoji2 className="w-[54px] h-[54px]" />
                  <div className="w-full">
                    <button
                      className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        signIn('discord')
                      }}
                    >
                      <IconDiscord className="w-[18px] h-[14px]" />
                      <span>绑定 Discord</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center text-sm text-black">绑定 Discord 账户</div>
                  <span className="text-[54px] leading-[54px]">👐</span>
                  <div className="w-full space-y-3">
                    <button
                      className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                      onClick={() => {
                        setStep(Step.Invite)
                      }}
                    >
                      <IconDiscord className="w-[18px] h-[14px]" />
                      <span>绑定完成！去邀请至频道</span>
                    </button>

                    <button
                      className="bg-transparent ring-1 ring-black w-full p-3 leading-[14px] rounded text-black text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black hover:text-basic-0"
                      onClick={() => {
                        unbind()
                      }}
                    >
                      <span>解绑 Discord{discordUsername ? `#${discordUsername}` : ''}</span>
                    </button>
                  </div>
                </>
              )
            ) : (
              <>
                <div className="text-center text-sm text-black">邀请进你有管理权限的频道噢~</div>
                <IconEmoji4 className="w-[54px] h-[54px]" />
                <div className="w-full">
                  <a
                    className="bg-black w-full p-3 leading-4 rounded text-white text-sm transition-all flex space-x-2 items-center justify-center hover:bg-black/80"
                    href="https://tezign.feishu.cn/docx/UJnIdJQkQoun93xOpqvcXpI3nih"
                    target="_blank"
                  >
                    <IconDiscord className="w-[18px] h-[14px]" />
                    <span>根据帮助文档邀请</span>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="fixed top-[20px] left-0">
        <button
          className="h-[38px] bg-white text-foreground inline-flex space-x-2 items-center rounded-r-xl shadow-md text-sm py-1.5 px-4 leading-[26px] transition-colors hover:text-basic-600"
          onClick={() => {
            setHomeViewType(HomeViewType.Default)
          }}
        >
          <IconArrowLeft className="w-4 h-4" />
          <span>返回</span>
        </button>
      </div>
    </>
  )
}
